/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Fade,
  InputAdornment,
  keyframes
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ArrowBack, 
  Email, 
  VpnKey,
  CheckCircle
} from '@mui/icons-material';
import api from '../../../axiosConfig';
import logo from '../../../Assets/img/logo.webp';

// Animaciones
const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px);
  }
  50% { 
    transform: translateY(-10px);
  }
`;

const scanAnimation = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

const successPulse = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

// Contenedor principal con fondo degradado
const ForgotPasswordContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #0a2e5c 0%, #051b38 100%)',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(2),
  backgroundImage: `
    radial-gradient(circle at 20% 80%, rgba(10, 46, 92, 0.4) 0%, transparent 60%),
    radial-gradient(circle at 80% 20%, rgba(10, 46, 92, 0.25) 0%, transparent 60%),
    radial-gradient(circle at 60% 60%, rgba(10, 46, 92, 0.18) 0%, transparent 50%)
  `,
}));

// Logo con efectos
const StyledLogo = styled('img')(({ theme, isMobile }) => ({
  width: isMobile ? 150 : 200,
  marginBottom: theme.spacing(3),
  filter: 'drop-shadow(0 0 20px rgba(10, 46, 92, 0.8))',
  animation: `${floatAnimation} 6s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    width: 120,
    marginBottom: theme.spacing(2),
  },
}));

// Contenedor del formulario con glass effect
const FormContainer = styled(Box)(({ theme, isMobile }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  padding: isMobile ? theme.spacing(3) : theme.spacing(4),
  borderRadius: 20,
  boxShadow: `
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    0 0 30px rgba(10, 46, 92, 0.2)
  `,
  position: 'relative',
  overflow: 'hidden',
  maxWidth: isMobile ? '90vw' : 400,
  width: '100%',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.1), transparent)',
    animation: `${scanAnimation} 8s linear infinite`,
    zIndex: 0,
    pointerEvents: 'none',
  },
}));

// Input personalizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 10,
    
    '& fieldset': {
      borderColor: 'rgba(10, 46, 92, 0.3)',
      borderWidth: 2,
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(10, 46, 92, 0.5)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: '#0a2e5c',
      boxShadow: '0 0 20px rgba(10, 46, 92, 0.4)',
    },
    
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: '#2c3e50',
    fontWeight: 600,
    zIndex: 15,
    
    '&.Mui-focused': {
      color: '#0a2e5c',
      fontWeight: 700,
    },
    
    '&.MuiFormLabel-filled': {
      color: '#2c3e50',
    },
  },
  
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#0a2e5c',
    fontSize: '1.4rem',
    filter: 'drop-shadow(0 0 3px rgba(10, 46, 92, 0.5))',
    transition: 'all 0.3s ease',
  },
  
  '& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
    transform: 'scale(1.1)',
    filter: 'drop-shadow(0 0 5px rgba(10, 46, 92, 0.6))',
  },
}));

// Botón principal
const StyledButton = styled(Button)(({ theme, isMobile, variant }) => ({
  width: '100%',
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  marginTop: theme.spacing(2),
  borderRadius: 12,
  fontSize: isMobile ? '0.9rem' : '1rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  zIndex: 10,
  
  ...(variant === 'primary' ? {
    background: 'linear-gradient(135deg, #0a2e5c, #051b38)',
    color: 'white',
    boxShadow: '0 8px 20px rgba(10, 46, 92, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s ease',
    },
    
    '&:hover::before': {
      left: '100%',
    },
    
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 25px rgba(10, 46, 92, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
      background: 'linear-gradient(135deg, #051b38, #082340)',
    },
    
    '&:disabled': {
      background: 'rgba(10, 46, 92, 0.5)',
      transform: 'none',
      '&::before': {
        display: 'none',
      },
    },
  } : {
    border: '2px solid rgba(10, 46, 92, 0.3)',
    color: '#0a2e5c',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    
    '&:hover': {
      backgroundColor: 'rgba(10, 46, 92, 0.05)',
      borderColor: '#0a2e5c',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 15px rgba(10, 46, 92, 0.2)',
    },
  }),
  
  '&:active': {
    transform: 'translateY(0)',
  },
}));

// Título con iconos
const StyledTitle = styled(Typography)(({ theme, isMobile }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  color: '#0a2e5c',
  fontSize: isMobile ? '1.3rem' : '1.6rem',
  fontWeight: 700,
  position: 'relative',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Descripción
const StyledDescription = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  color: '#555',
  fontSize: '0.95rem',
  lineHeight: 1.5,
  position: 'relative',
  zIndex: 10,
}));

// Alerta de éxito personalizada
const SuccessContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: 16,
  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(67, 160, 71, 0.05))',
  border: '2px solid rgba(76, 175, 80, 0.3)',
  position: 'relative',
  zIndex: 10,
  animation: `${successPulse} 2s ease-in-out infinite`,
  
  '& .success-icon': {
    fontSize: '3rem',
    color: '#4caf50',
    marginBottom: theme.spacing(2),
    filter: 'drop-shadow(0 0 10px rgba(76, 175, 80, 0.4))',
  },
  
  '& .success-title': {
    color: '#2e7d32',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  
  '& .success-message': {
    color: '#388e3c',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
}));

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await api.post('/conductor/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      console.log('Error completo:', err.response);
      
      if (err.response && err.response.data) {
        const errorMessage = err.response.data.message || 'Error al enviar la solicitud';
        setError(errorMessage);
      } else if (err.request) {
        setError('No se pudo conectar con el servidor. Por favor intenta nuevamente.');
      } else {
        setError('Error al procesar la solicitud. Por favor intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <ForgotPasswordContainer>
      <StyledLogo 
        src={logo} 
        alt="Mogotax Logo" 
        isMobile={isMobile}
      />
      
      <FormContainer isMobile={isMobile}>
        <StyledButton
          variant="secondary"
          startIcon={<ArrowBack />}
          onClick={handleBackToLogin}
          isMobile={isMobile}
          sx={{ mb: 3, textTransform: 'none', justifyContent: 'flex-start' }}
        >
          Volver al inicio
        </StyledButton>
        
        <StyledTitle variant="h4" isMobile={isMobile}>
          <VpnKey sx={{ fontSize: isMobile ? '1.3rem' : '1.6rem' }} />
          Recuperar Contraseña
        </StyledTitle>
        
        {success ? (
          <SuccessContainer>
            <CheckCircle className="success-icon" />
            <Typography variant="h6" className="success-title">
              ¡Correo enviado exitosamente!
            </Typography>
            <Typography className="success-message">
              Si tu correo está registrado, recibirás un enlace para restablecer tu contraseña en los próximos minutos. 
              Revisa también tu carpeta de spam.
            </Typography>
            <StyledButton
              variant="primary"
              onClick={handleBackToLogin}
              isMobile={isMobile}
              sx={{ mt: 3 }}
            >
              Volver al inicio de sesión
            </StyledButton>
          </SuccessContainer>
        ) : (
          <>
            <StyledDescription>
              Ingresa tu correo electrónico registrado y te enviaremos un enlace seguro para restablecer tu contraseña
            </StyledDescription>
            
            <Box component="form" onSubmit={handleSubmit} sx={{ position: 'relative', zIndex: 10 }}>
              <StyledTextField
                fullWidth
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
              
              {error && (
                <Fade in={Boolean(error)}>
                  <Alert 
                    severity={error.includes('revisa tu correo') ? 'warning' : 'error'} 
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                      position: 'relative',
                      zIndex: 10,
                    }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}
              
              <StyledButton
                type="submit"
                variant="primary"
                disabled={loading}
                isMobile={isMobile}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Enviando enlace...
                  </Box>
                ) : (
                  'Enviar enlace de recuperación'
                )}
              </StyledButton>
            </Box>
          </>
        )}
      </FormContainer>
    </ForgotPasswordContainer>
  );
}

export default ForgotPasswordPage;